import styled from 'styled-components';
import { Button } from '@mui/material';
import { useEffect } from 'react';
import { Form, Formik, useFormikContext } from 'formik';

import { ASCVD_Validation } from 'modules/report/utils/calculators-validation-utils';
import {
  booleanItems,
  genderItems,
  smokeDurationItems,
  smokerItems
} from 'modules/report/utils/calculators-schema-utils';
import {
  FormikSelectField,
  FormikInput
} from 'modules/report/components/report-components/therapeutic-adjustment/calculators';
import CircularLoading from 'modules/components/common/loading/circular-loading';
import { useAscvd } from 'modules/report/hooks/useAscvd';

const modifyResultFormat = (string) => string.replace('%', ' %');

export const ASCVD = ({ isMale, userAge }) => {
  const initialValues = {
    onAspirinTherapy: null,
    onAStatin: null,
    smoker: null,
    quitSmoking: null,
    historyOfDiabetes: null,
    ldlCholesterol: null,
    hdlCholesterol: null,
    systolicBloodPressure: null,
    diastolicBloodPressure: null,
    sex: isMale ? 1 : isMale !== null ? 0 : null,
    currentAge: userAge >= 20 && userAge <= 79 ? userAge : null,
    totalCholesterol: null,
    onHypertensionTreatment: null
  };
  const ascvd = useAscvd();
  const { optimalRisk, lifeTimeRisk, currentRisk } = ascvd?.data?.data || {};

  const handleSubmit = (values) => {
    let data;
    if (values.smoker === 2) {
      data = { ...values, quitSmoking: 0 };
    } else {
      data = values;
    }
    ascvd.mutate({ data });
  };

  const getFieldErrorNames = (formikErrors) => {
    const transformObjectToDotNotation = (obj, prefix = '', result = []) => {
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (!value) return;
        const nextKey = prefix ? `${prefix}.${key}` : key;
        if (typeof value === 'object') {
          transformObjectToDotNotation(value, nextKey, result);
        } else {
          result.push(nextKey);
        }
      });
      return result;
    };
    return transformObjectToDotNotation(formikErrors);
  };

  const ScrollToFieldError = () => {
    const { submitCount, isValid, errors, setFieldTouched } =
      useFormikContext();

    useEffect(() => {
      if (isValid || submitCount === 0) return;

      const fieldErrorNames = getFieldErrorNames(errors);

      if (fieldErrorNames.length > 0) {
        const firstErrorField = fieldErrorNames[0];
        const element = document.querySelector(`[name="${firstErrorField}"]`);

        if (element) {
          setFieldTouched(firstErrorField, true);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, [submitCount, isValid]);

    return null;
  };

  return (
    <Container hasResult={lifeTimeRisk || optimalRisk}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ASCVD_Validation}
        onSubmit={handleSubmit}
      >
        {(props) => {
          const {
            values: { currentAge, ldlCholesterol },
            isValid,
            dirty,
            submitCount
          } = props;

          const age_20to39 = currentAge >= 20 && currentAge <= 39;
          const age_60to79 = currentAge >= 60 && currentAge <= 79;

          const ageDescription = age_20to39
            ? 'This calculator only provides 10-year risk estimates for individuals 40 to 79 years of age.'
            : age_60to79
            ? 'Lifetime Risk Calculator only provides lifetime risk estimates for individuals 20 to 59 years of age.'
            : 'Age must be between 20-79.';

          return (
            <Form dir="ltr">
              <ScrollToFieldError />
              <FormContainer>
                <div className="form__inputs">
                  <FormikInput
                    {...props}
                    required
                    name="currentAge"
                    length="3"
                    label="Current Age"
                    description={ageDescription}
                    descriptionColor={(age_20to39 || age_60to79) && '#8D4CD5'}
                  />
                  <FormikSelectField
                    {...props}
                    name="sex"
                    items={genderItems}
                    discription="select your sex"
                    label="Sex* :"
                  />
                  <FormikInput
                    {...props}
                    length="3"
                    required
                    name="systolicBloodPressure"
                    label="Systolic Blood Pressure (mm Hg)"
                    description="Value must be between 90-200."
                  />
                  <FormikInput
                    {...props}
                    length="3"
                    required
                    name="diastolicBloodPressure"
                    label="Diastolic Blood Pressure (mm Hg)"
                    description="Value must be between 60-130."
                  />
                  <FormikInput
                    {...props}
                    required
                    length="3"
                    name="totalCholesterol"
                    label="Total Cholesterol (mg/dL)"
                    description="Value must be between 130-320."
                  />
                  <FormikInput
                    {...props}
                    required
                    length="3"
                    name="hdlCholesterol"
                    label="HDL Cholesterol (mg/dL)"
                    description="Value must be between 20-100."
                  />
                  <FormikInput
                    {...props}
                    required={false}
                    length="3"
                    name="ldlCholesterol"
                    label="LDL Cholesterol (mg/dL)"
                    description="Value must be between 30-300."
                  />
                  <FormikSelectField
                    name="historyOfDiabetes"
                    {...props}
                    items={booleanItems}
                    discription="select your history of diabetes"
                    label="History of Diabetes? *"
                  />
                  <FormikSelectField
                    name="smoker"
                    {...props}
                    items={smokerItems}
                    discription="select your smoker"
                    label="Smoker? *"
                  />
                  {props.values.smoker == 1 && (
                    <FormikSelectField
                      name="quitSmoking"
                      column={true}
                      {...props}
                      items={smokeDurationItems}
                      label="How long ago did patient quit smoking? *"
                      centerLabel
                    />
                  )}
                  <FormikSelectField
                    name="onHypertensionTreatment"
                    {...props}
                    items={booleanItems}
                    label="On Hypertension Treatment? *"
                  />
                  <FormikSelectField
                    name="onAStatin"
                    {...props}
                    items={booleanItems}
                    label="On a Statin?"
                  />
                  <FormikSelectField
                    {...props}
                    name="onAspirinTherapy"
                    items={booleanItems}
                    label="On Aspirin Therapy?"
                  />
                </div>
                <CalculateBtn
                  type="submit"
                  isActive={submitCount == 0 ? true : dirty && isValid}
                  disabled={
                    submitCount == 0
                      ? false
                      : !(dirty && isValid) || ascvd.isLoading
                  }
                >
                  {!ascvd.isLoading ? (
                    <span>Calculate</span>
                  ) : (
                    <span className="circularLoading">
                      <CircularLoading
                        color="#3CC0C3"
                        width={'28px'}
                        height="28px"
                      />
                    </span>
                  )}
                </CalculateBtn>
                <div className="result">
                  Lifetime ASCVD Risk:{' '}
                  <span className="result-num">
                    {lifeTimeRisk ? modifyResultFormat(lifeTimeRisk) : '- %'}
                  </span>
                </div>
                <div className="result">
                  Optimal ASCVD Risk:{' '}
                  <span className="result-num">
                    {optimalRisk ? modifyResultFormat(optimalRisk) : '- %'}
                  </span>
                </div>
                <div>
                  <div className="result">
                    Current 10-Year ASCVD Risk:{' '}
                    <span className="result-num">
                      {currentRisk ? modifyResultFormat(currentRisk) : '- %'}
                    </span>
                  </div>
                  <p className="input-text">
                    {currentAge < 40 && !!currentAge
                      ? `This calculator only provides 10-year risk estimates for individuals 40-79 years of ago.`
                      : !!ldlCholesterol && ldlCholesterol < 70
                      ? `10-year ASCVD risk not available for patients with LDL-C <70 mg/dL.`
                      : ''}
                  </p>
                </div>
              </FormContainer>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};
const FormContainer = styled.div`
  display: grid;
  gap: 28px;

  .form__inputs {
    display: grid;
    gap: 28px;
  }

  @media screen and (min-width: 1024px) {
    margin-top: 39px;
    grid-template-columns: 100%;
    margin-bottom: 80px;
    gap: 32px;
    .form__inputs {
      display: grid;
      gap: 14px;
    }
  }
`;
const Container = styled.div`
  .circularLoading {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 10px;
  }

  .result {
    padding: 0 30px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    background-color: #f5f5f5;
    border: ${({ hasResult }) =>
      hasResult ? '1px solid #3CC0C3' : '1px solid #FFFFFF'};
    box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.16);
    color: ${({ hasResult }) => (!hasResult ? '#707070' : '#6F4D9F')};
    text-align: center;
    font-size: 14px;
    font-family: 'dana-bold';
    text-transform: capitalize;
    border-radius: 14px;
  }

  @media screen and (min-width: 1024px) {
    .result {
      height: 64px;
      font-size: 24px !important;
      width: 60% !important;
      margin: 0 auto !important;
      background-color: #ffffff !important;
    }

    .input-text {
      color: #6f4d9f;
      font-size: 16px !important;
      width: 60% !important;
      margin: 10px auto !important;
    }
  }

  .result-num {
    font-size: 18px;
  }

  .input-text {
    color: #6f4d9f;
    font-size: 12px;
    margin-top: 6px;
  }
`;

const CalculateBtn = styled(Button)`
  position: relative;
  height: 48px;
  width: 100%;
  border-radius: 16px !important;
  background-color: ${({ isActive }) =>
    isActive ? '#753CBE !important' : '#f5f5f5 !important'};
  color: ${({ isActive }) =>
    isActive ? '#FFFFFF !important' : 'rgba(0, 0, 0, 0.26) !important'};
  border: ${({ isActive }) => (isActive ? '' : '1px solid #FFFFFF !important')};
  box-shadow: ${({ isActive }) =>
    isActive ? '' : '1px 1px 2px rgba(0, 0, 0, 0.16) !important'};
  text-align: center !important;
  font-size: 16px !important;
  font-family: 'dana-bold';
  text-transform: unset !important;
  @media screen and (min-width: 1024px) {
    height: 64px;
    font-size: 24px !important;
    width: 60% !important;
    margin: 37px auto 0 !important;
    font-family: 'dana-bold';
    span {
      color: ${({ isActive }) => (isActive ? '#FFFFFF !important' : '#acacac')};
    }
  }
`;
